import React from 'react';
import { Box, Text, Badge, Divider } from '@chakra-ui/react';
import { BinderSectionData } from '../hooks/useBinderSectionData';
import { useBinderCardColors } from '../hooks/useBinderCardColors';

interface RemarksCardProps {
  sectionData: BinderSectionData;
}

const RemarksCard: React.FC<RemarksCardProps> = ({ sectionData }) => {
  const { data } = sectionData;
  const { remarks = [], totalRemarks = 0 } = data || {};
  const colors = useBinderCardColors();

  // Handle case when no remarks
  if (totalRemarks === 0) {
    return (
      <Box p={4} borderRadius="md" bg={colors.emptyFieldBgColor}>
        <Text color="gray.500" fontStyle="italic" textAlign="center">
          No remarks available
        </Text>
      </Box>
    );
  }

  // Color scheme for different statuses
  const getStatusColor = (status: string) => {
    const colorMap: { [key: string]: string } = {
      approved: 'green',
      created: 'blue',
      pending: 'yellow',
      rejected: 'red',
      unknown: 'gray',
    };
    return colorMap[status] || 'gray';
  };

  // Format text helper
  const formatText = (text: string) => {
    if (!text) return text;
    return text.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <Box
      borderRadius="md"
      borderWidth="1px"
      borderColor={colors.borderColor}
      overflow="hidden"
      bg="white"
    >
      {/* Header */}
      <Box
        p={4}
        bg={colors.emptyFieldBgColor}
        borderBottomWidth="1px"
        borderColor={colors.borderColor}
      >
        <Text fontSize="sm" color={colors.descriptionColor}>
          {totalRemarks} remark{totalRemarks !== 1 ? 's' : ''}
        </Text>
      </Box>

      {/* Remarks List */}
      <Box p={0}>
        {remarks.map((remark: any, index: number) => (
          <Box key={index}>
            <Box p={4}>
              <Box display="flex" alignItems="flex-start" gap={3}>
                {/* Index indicator */}
                <Box
                  minWidth="32px"
                  height="24px"
                  borderRadius="full"
                  bg={`${getStatusColor(remark.status)}.100`}
                  color={`${getStatusColor(remark.status)}.600`}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="xs"
                  fontWeight="bold"
                  mt="2px"
                >
                  #{remark.index}
                </Box>

                {/* Remark content */}
                <Box flex="1">
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Badge
                      colorScheme={getStatusColor(remark.status)}
                      size="sm"
                    >
                      {remark.status}
                    </Badge>
                    {remark.validationKey && (
                      <Badge colorScheme="gray" size="sm" variant="outline">
                        {remark.validationKey}
                      </Badge>
                    )}
                    <Text fontSize="xs" color={colors.descriptionColor}>
                      {formatText(remark.itemKey)}
                    </Text>
                  </Box>
                  <Text
                    fontSize="sm"
                    color={colors.textColor}
                    lineHeight="1.5"
                    whiteSpace="pre-line"
                  >
                    {remark.content}
                  </Text>
                </Box>
              </Box>
            </Box>

            {/* Divider between remarks (except last one) */}
            {index < remarks.length - 1 && (
              <Divider borderColor={colors.borderColor} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default RemarksCard;
